import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Player from '..';
import { ScriptService } from '../../../controllers/ScriptService';
import { HeaderDisplayType, HeaderInteractionType, PvrRecording } from '../../../interfaces';
import { PlayData } from '../../../queries/ats/playback/api';
import { Recording, SingleRecording } from '../../../queries/ats/recordings/types/recording';
import { wrapAssetAsAltiboxAsset } from '../../../utils/altiboxassetUtils';
import Header from '../../../views/app/Header';
import MobileLock from '../../../views/app/MobileLock';
import NoSupport from '../../NoSupport';
import { PlayerFullscreenPortal } from '../../player-fullscreen-portal';
import { PlayerError } from '../ats-player/player-error';
import { PlayerLayout } from '../player-layout';
import { getRecordingStream } from './get-recording-stream';
import { RecordingPlayerCard } from './recording-player-card';
import { useRecordingAutoplay } from './use-recording-autoplay';

export interface RecordingClientPlayerProps {
  data: PlayData;
  recording: SingleRecording;
  recordings: Recording[];
  legacyRecording: PvrRecording;
  position: number;
  reportConsumption: (position: number) => void;
  stopConsumption: (position: number) => void;
}

export function RecordingClientPlayer({
  data,
  recording,
  recordings,
  legacyRecording,
  position,
  reportConsumption,
  stopConsumption,
}: RecordingClientPlayerProps) {
  const { t } = useTranslation();

  const [error, setError] = useState<unknown>();

  const stream = useMemo(() => {
    return getRecordingStream(recording, data, position);
  }, [data, position, recording]);

  const history = useHistory();
  const shouldHideControls = useSelector((state) => state.app.shouldFadeOut);

  const autoplay = useRecordingAutoplay(recording, recordings);

  const setPlayerError = useCallback(
    (title: string, message: string) => {
      setError(new PlayerError(title, message, { sessionId: data.sessionId, position: position }));
    },
    [data.sessionId, position],
  );

  const title =
    recording.type === 'linked'
      ? `${recording.name} - S${recording.season.sequenceNumber} E${recording.episode.sequenceNumber}`
      : recording.name;

  if (ScriptService.onMobile()) {
    const deepLink = 'altibox://';
    return (
      <div className="main-frame">
        <Header displayType={HeaderDisplayType.NoStickyGradient} interactionType={HeaderInteractionType.CloseOnly} />
        <MobileLock deepLink={deepLink} /> :
      </div>
    );
  }

  if (!ScriptService.isCurrentBrowserSupported()) {
    return (
      <PlayerLayout title={title}>
        <NoSupport />
      </PlayerLayout>
    );
  }

  if (error instanceof PlayerError) {
    throw error;
  }

  if (error instanceof Error) {
    throw new PlayerError(t('unknown error'), error.message, {
      sessionId: data.sessionId,
      position: position,
      cause: error,
    });
  }

  if (error !== undefined) {
    throw new PlayerError(t('unknown error'), JSON.stringify(error), {
      sessionId: data.sessionId,
      position: position,
      cause: error,
    });
  }

  return (
    <PlayerLayout title={title}>
      <PlayerFullscreenPortal>
        <RecordingPlayerCard recording={recording} />
      </PlayerFullscreenPortal>

      <Player
        stream={stream}
        autoplay={autoplay}
        onBookmark={(event) => reportConsumption(event.position)}
        onUnmount={(event) => stopConsumption(event.position)}
        onEnded={() => history.goBack()}
        onAlert={() => setPlayerError(t('an_error_occured'), t('error sorry message'))}
        onConcurrencyError={() =>
          setPlayerError(t('concurrency title'), `${t('concurrency message1')} ${t('concurrency message2')}`)
        }
        onPlayerError={setError}
        hideControls={shouldHideControls}
        currentAsset={wrapAssetAsAltiboxAsset(legacyRecording)}
      />
    </PlayerLayout>
  );
}
